import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { User } from "../types";
import { authAPI } from "../services/api";

interface AuthContextType {
  user: User | null;
  login: (token: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        // Fetch fresh profile data
        authAPI
          .getProfile()
          .then(({ user: profileUser }) => {
            setUser(profileUser);
            localStorage.setItem("user", JSON.stringify(profileUser));
          })
          .catch(() => {
            // Token might be invalid, clear storage
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (token: string) => {
    try {
      localStorage.setItem("token", token);

      // Fetch user profile
      const profileData = await authAPI.getProfile();
      setUser(profileData.user);
      localStorage.setItem("user", JSON.stringify(profileData.user));
    } catch (error) {
      localStorage.removeItem("token");
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
